import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schema, supabase } from '@radiology/api-client';

/**
 * Certifications data access.
 *
 * There is no application server (decision #9). These talk straight to PostgREST
 * and RLS decides what comes back — an employee's query and a manager's query are
 * the same SQL, scoped differently by the database.
 *
 * So none of these functions filter by hospital, section or owner. If you find
 * yourself adding such a filter for security rather than for the UI, that belongs
 * in a policy, not here.
 */

export type CertificateStatus = 'pending' | 'valid' | 'rejected' | 'expiring soon' | 'expired';

export interface CertificateComment {
  comment: string;
  authorName?: string;
  authorRole?: string;
  createdAt?: string;
}

export interface Certificate {
  id: string;
  name: string;
  expiry_date: string;
  file_name: string;
  file_path: string;
  status: CertificateStatus;
  employee_id: string;
  username: string;
  first_name: string;
  last_name: string;
  created_at: string;
  approved_date: string | null;
  hospital: string;
  section: string;
  title: string;
  badge_number: string;
  comment: CertificateComment[] | null;
}

const KEY = ['certifications', 'certificates'] as const;

/**
 * PostgREST caps a response at 1,000 rows and does NOT signal that it truncated —
 * it just returns fewer. An admin is unscoped and sees 3,239 certificates, so a
 * single call would silently show them 1,000 and look perfectly normal.
 *
 * Every read that can exceed 1,000 rows has to page.
 */
const PAGE = 1000;

export function useCertificates(status?: CertificateStatus | CertificateStatus[]) {
  return useQuery({
    queryKey: [...KEY, status ?? 'all'],
    queryFn: async (): Promise<Certificate[]> => {
      const all: Certificate[] = [];

      for (let from = 0; ; from += PAGE) {
        let query = schema('certifications')
          .from('certificates')
          .select('*')
          .order('expiry_date', { ascending: true })
          .range(from, from + PAGE - 1);

        if (Array.isArray(status)) query = query.in('status', status);
        else if (status) query = query.eq('status', status);

        const { data, error } = await query.returns<Certificate[]>();
        if (error) throw new Error(error.message);

        all.push(...(data ?? []));
        if (!data || data.length < PAGE) break;
      }

      return all;
    },
  });
}

/**
 * A short-lived signed URL for one certificate file.
 *
 * The bucket is private and storage RLS derives access from the certificate row,
 * so this only succeeds for a file the caller is allowed to see — asking for
 * someone else's returns "Object not found" rather than confirming it exists.
 *
 * The legacy app served these as static files from /uploads with no
 * authorization at all, and filenames are millisecond timestamps, so anyone who
 * guessed one could download anybody's certificate without logging in.
 */
export async function getFileUrl(fileName: string): Promise<string> {
  const { data, error } = await supabase()
    .storage.from('certificates')
    .createSignedUrl(fileName, 120);

  if (error || !data) throw new Error('That file could not be opened.');
  return data.signedUrl;
}

/**
 * ---------------------------------------------------------------------------
 * Requirements — the CR's model.
 *
 * The dashboard used to count FILES. It now counts REQUIREMENTS: one row per
 * certificate type the signed-in employee must hold, whatever number of
 * versions of it they have uploaded over the years (CERT-05).
 *
 * The difference is not cosmetic. A real technologist in this database has 18
 * certificates with status `expired` and showed "18 Expired" — but 16 of them
 * are superseded versions of types they have since renewed. Their true position
 * is two expired requirements.
 * ---------------------------------------------------------------------------
 */

/**
 * `Missing` is NOT one of the CR's four statuses.
 *
 * The CR defines Valid, Expiring Soon, Expired and Awaiting Review, and says
 * nothing about a requirement with no approved certificate behind it. Folding it
 * into Expired would overstate Expired against the CR's own definition
 * ("determined from the approved certificate"), and dropping it would hide a
 * real compliance gap. It is therefore its own state, surfaced separately.
 * Flagged for business confirmation.
 *
 * It covers three situations, all of which leave the employee non-compliant:
 * nothing ever uploaded, a first upload still awaiting review, and one that was
 * rejected. An earlier draft called this 'Not uploaded', which was wrong for two
 * of the three — a pending certificate would have read "Not uploaded · In
 * review" on the same row.
 */
export type RequirementStatus = 'Valid' | 'Expiring Soon' | 'Expired' | 'Missing';

export interface Requirement {
  username: string;
  title: string | null;
  certificate_type: string;
  list_name: string;
  all_titles: boolean;
  current_certificate_id: string | null;
  current_expiry_date: string | null;
  current_file_name: string | null;
  status: RequirementStatus;
  /**
   * A renewal is sitting with the supervisor.
   *
   * Deliberately separate from `status`, per CERT-06: a pending renewal does
   * not clear Expired. A requirement can be both at once, and the two dashboard
   * cards can legitimately count the same row.
   */
  awaiting_review: boolean;
  pending_certificate_id: string | null;
  /**
   * The last attempt that was rejected, if nothing is pending for this type.
   * Drives CERT-02's "Rejected keeps Replace".
   */
  rejected_certificate_id: string | null;
  /** How many versions exist in history, current and superseded. */
  version_count: number;
}

const REQUIREMENTS = ['certifications', 'my_requirements'] as const;

export function useMyRequirements() {
  return useQuery({
    queryKey: REQUIREMENTS,
    queryFn: async (): Promise<Requirement[]> => {
      const { data, error } = await schema('certifications')
        .from('my_requirement_status')
        .select('*')
        .order('certificate_type')
        .returns<Requirement[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** The four KPI counts, exactly as CERT-01 names them, plus the extra state. */
export function useRequirementTotals() {
  const { data, isLoading } = useMyRequirements();

  const totals = useMemo(() => {
    const rows = data ?? [];
    const count = (s: RequirementStatus) => rows.filter(r => r.status === s).length;
    return {
      valid: count('Valid'),
      expiringSoon: count('Expiring Soon'),
      expired: count('Expired'),
      // Counted independently of status — see CERT-06.
      awaitingReview: rows.filter(r => r.awaiting_review).length,
      missing: count('Missing'),
      /**
       * Expired requirements the employee can actually do something about.
       *
       * Excludes those whose renewal is already in review: still Expired
       * (CERT-06), still on the card, but CERT-02 shows them no action, so
       * prompting them to renew would send them hunting for a button that is
       * not there.
       */
      expiredActionable: rows.filter(r => r.status === 'Expired' && !r.awaiting_review).length,
      total: rows.length,
    };
  }, [data]);

  return { totals, isLoading };
}

/**
 * Certificates the employee holds that are not required of them.
 *
 * These stopped counting towards the KPIs when requirements replaced files. They
 * have not stopped existing, and this is what keeps them on screen rather than
 * letting a certificate silently vanish after the change.
 */
export interface ExtraCertificate {
  id: string;
  certificate_type: string;
  status: CertificateStatus;
  expiry_date: string;
  file_name: string;
}

export function useMyExtraCertificates() {
  return useQuery({
    queryKey: ['certifications', 'my_extra_certificates'],
    queryFn: async (): Promise<ExtraCertificate[]> => {
      const { data, error } = await schema('certifications')
        .from('my_extra_certificates')
        .select('*')
        .returns<ExtraCertificate[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/**
 * CERT-11 — what this employee may upload.
 *
 * The General (All Titles) types plus the types for their own Title, and nothing
 * else. Replaces the old dropdown, which offered every name in the system to
 * everybody regardless of profession.
 */
export function useMyCertificateOptions() {
  return useQuery({
    queryKey: ['certifications', 'my_certificate_options'],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await schema('certifications')
        .from('my_certificate_options')
        .select('certificate_type')
        .order('certificate_type')
        .returns<{ certificate_type: string }[]>();
      if (error) throw new Error(error.message);
      return (data ?? []).map(r => r.certificate_type);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The certificate names lookup, as the legacy upload form's dropdown used.
 * Readable by anyone in the module.
 */
export function useCertificateNames() {
  return useQuery({
    queryKey: ['certifications', 'certificate_names'],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await schema('certifications')
        .from('certificate_names')
        .select('name')
        .order('name')
        .returns<{ name: string }[]>();
      if (error) throw new Error(error.message);
      return (data ?? []).map(r => r.name);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Legacy accepted these four and nothing else. */
export const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif'];

async function putFile(file: File): Promise<string> {
  // Legacy filenames were `Date.now() + extname`, i.e. guessable by design — and
  // the directory was served statically with no authorization, so guessing one
  // was enough to read somebody's certificate. A uuid removes the guessability;
  // the private bucket removes the exposure.
  const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
  const path = `${crypto.randomUUID()}${ext}`;

  const { error } = await supabase()
    .storage.from('certificates')
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw new Error(error.message);

  return path;
}

/**
 * Everything a certificate change can move.
 *
 * The requirement views are derived from the certificate rows, so any write has
 * to drop them too — otherwise a renewal submits successfully and the dashboard
 * keeps showing the pre-renewal position until the page is reloaded.
 */
function invalidateCertificates(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: KEY });
  void queryClient.invalidateQueries({ queryKey: ['certifications', 'status_summary'] });
  void queryClient.invalidateQueries({ queryKey: REQUIREMENTS });
  void queryClient.invalidateQueries({ queryKey: ['certifications', 'my_extra_certificates'] });
}

/**
 * Upload a certificate — and, unchanged, renew one.
 *
 * CERT-03 and the CR's certificate-history rule make a renewal an INSERT, not an
 * update: "A renewal creates a new certificate record/version" and "Do not
 * delete historical versions". So renewing is this same call with the type
 * fixed by the caller — there is no separate renewal mutation, because a
 * renewal that behaved differently from an upload would be the bug.
 *
 * Contrast `useReplaceCertificate`, which genuinely does overwrite: a rejected
 * certificate was never approved, so there is no history to preserve.
 *
 * Note what is NOT sent: employee id, username, name, hospital, section, title,
 * badge number. The legacy form sent all eight from client state, and `hospital`
 * and `section` are what decide which supervisor reviews it. A database trigger
 * stamps them from the caller's profile now, so this cannot file a certificate
 * under someone else's name or into someone else's queue.
 */
export function useUploadCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { name: string; expiryDate: string; file: File }) => {
      const path = await putFile(input.file);

      const { error } = await schema('certifications').from('certificates').insert({
        name: input.name,
        expiry_date: input.expiryDate,
        file_name: path,
        file_path: path,
        status: 'pending',
      });

      if (error) {
        // Do not leave the object behind if the row was refused.
        await supabase().storage.from('certificates').remove([path]);
        throw new Error(error.message);
      }
    },
    onSuccess: () => invalidateCertificates(queryClient),
  });
}

/**
 * Replace the file on a rejected certificate and send it back for review.
 *
 * Mirrors the legacy `PATCH /certificates/:id/replace`: swap the file, allow the
 * name and expiry to change, append the employee's comment to the history, reset
 * the status to pending. The old object is removed afterwards, as the legacy
 * service did with `unlinkAsync`.
 */
export function useReplaceCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      expiryDate: string;
      file: File;
      comment?: string;
      existing?: CertificateComment[] | null;
      previousPath?: string | null;
      authorName: string;
    }) => {
      const path = await putFile(input.file);

      const patch: Record<string, unknown> = {
        name: input.name,
        expiry_date: input.expiryDate,
        file_name: path,
        file_path: path,
        status: 'pending',
      };

      if (input.comment?.trim()) {
        patch.comment = [
          ...(input.existing ?? []),
          {
            comment: input.comment.trim(),
            authorName: input.authorName,
            authorRole: 'employee',
            createdAt: new Date().toISOString().slice(0, 10),
          },
        ];
      }

      const { error } = await schema('certifications')
        .from('certificates')
        .update(patch)
        .eq('id', input.id);

      if (error) {
        await supabase().storage.from('certificates').remove([path]);
        throw new Error(error.message);
      }

      if (input.previousPath && input.previousPath !== path) {
        await supabase().storage.from('certificates').remove([input.previousPath]);
      }
    },
    onSuccess: () => invalidateCertificates(queryClient),
  });
}

/** Withdraw an upload that has not been reviewed yet. */
export function useWithdrawCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; path?: string | null }) => {
      const { error } = await schema('certifications')
        .from('certificates')
        .delete()
        .eq('id', input.id);
      if (error) throw new Error(error.message);

      if (input.path) {
        await supabase().storage.from('certificates').remove([input.path]);
      }
    },
    onSuccess: () => invalidateCertificates(queryClient),
  });
}

export interface StatusSummaryRow {
  hospital: string;
  section: string;
  status: CertificateStatus;
  total: number;
  overdue: number;
  expiring_within_45_days: number;
}

/** Aggregates for the dashboards, so counting does not pull every row. */
export function useStatusSummary() {
  return useQuery({
    queryKey: ['certifications', 'status_summary'],
    queryFn: async (): Promise<StatusSummaryRow[]> => {
      const { data, error } = await schema('certifications')
        .from('status_summary')
        .select('*')
        .returns<StatusSummaryRow[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/**
 * Approve or reject.
 *
 * The database enforces the rules: only a pending certificate can be reviewed,
 * only a supervisor or admin may do it, a rejection must carry a comment, and
 * approval stamps the date. A failure here is the trigger talking, so its message
 * is worth showing.
 */
export function useReviewCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      decision: 'valid' | 'rejected';
      comment?: string;
      existing?: CertificateComment[] | null;
      reviewerName: string;
    }) => {
      const patch: Record<string, unknown> = { status: input.decision };

      if (input.decision === 'rejected') {
        // Comments are append-only history, not a single field.
        patch.comment = [
          ...(input.existing ?? []),
          {
            comment: input.comment ?? '',
            authorName: input.reviewerName,
            authorRole: 'supervisor',
            createdAt: new Date().toISOString().slice(0, 10),
          },
        ];
      }

      const { error } = await schema('certifications')
        .from('certificates')
        .update(patch)
        .eq('id', input.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateCertificates(queryClient),
  });
}

export const STATUS_TONE: Record<
  CertificateStatus,
  'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  valid: 'success',
  'expiring soon': 'warning',
  expired: 'error',
  pending: 'info',
  rejected: 'error',
};
